import React from 'react';
import { ChevronRightIcon, UploadIcon, CheckCircleIcon } from '../shared/icons';
import ExpandedDocSection from '../ExpandedDocSection';
import styles from './index.module.scss';

const TABLE_HEADERS = [
  'RFQ Number', 'Shipping Liner', 'Service Provider Name',
  'From', 'To', 'Container Type', 'No of containers',
  'DESK Type', 'Inco Term', 'Bidding Event',
];

const COLUMN_COUNT = TABLE_HEADERS.length + 2; // toggle + actions

const BookingTable = ({
  data,
  expandedRow,
  page,
  totalItems,
  pageSize,
  isFetching,
  onToggleRow,
  onPageChange,
  onUploadBookingCopy,
  onUploadBL,
  onViewBL,
  onSaveDoc,
  onDeleteDoc,
  updateSubmitting,
  deleteSubmitting,
  pendingDocs,
  onSavePendingDoc,
  onDiscardPendingDoc,
  saveSubmitting,
}) => {
  const total = totalItems != null ? totalItems : data.length;
  const size = pageSize || 20;
  const totalPages = Math.ceil(total / size);
  const from = total === 0 ? 0 : (page - 1) * size + 1;
  const to = Math.min(page * size, total);

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const visible = 5;
    const half = Math.floor(visible / 2);
    let start = Math.max(1, page - half);
    let end = Math.min(totalPages, start + visible - 1);
    if (end - start < visible - 1) start = Math.max(1, end - visible + 1);

    pages.push(
      <button key="prev" className={styles.pageBtn} disabled={page === 1} onClick={() => onPageChange(page - 1)}>‹</button>
    );
    if (start > 1) {
      pages.push(<button key={1} className={`${styles.pageBtn} ${page === 1 ? styles.active : ''}`} onClick={() => onPageChange(1)}>1</button>);
      if (start > 2) pages.push(<span key="dots1" className={`${styles.pageBtn} ${styles.dots}`}>...</span>);
    }
    for (let p = start; p <= end; p++) {
      pages.push(
        <button key={p} className={`${styles.pageBtn} ${p === page ? styles.active : ''}`} onClick={() => onPageChange(p)}>{p}</button>
      );
    }
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push(<span key="dots2" className={`${styles.pageBtn} ${styles.dots}`}>...</span>);
      pages.push(<button key={totalPages} className={`${styles.pageBtn} ${page === totalPages ? styles.active : ''}`} onClick={() => onPageChange(totalPages)}>{totalPages}</button>);
    }
    pages.push(
      <button key="next" className={styles.pageBtn} disabled={page === totalPages} onClick={() => onPageChange(page + 1)}>›</button>
    );
    return pages;
  };

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            <th />
            {TABLE_HEADERS.map(h => <th key={h}>{h}</th>)}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody className={styles.tbody}>
          {isFetching ? (
            <tr>
              <td colSpan={COLUMN_COUNT} style={{ textAlign: 'center', padding: '32px' }}>Loading...</td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={COLUMN_COUNT} style={{ textAlign: 'center', padding: '32px' }}>No records found.</td>
            </tr>
          ) : (
            data.map(row => {
              const hasBookingCopy = !!(row.has_booking_copy && row.docs && row.docs.length > 0);
              return (
              <React.Fragment key={row.id}>
                <tr className={hasBookingCopy ? styles.hasBookingCopy : ''}>
                  <td>
                    <button
                      className={`${styles.toggleBtn} ${expandedRow === row.id ? styles.open : ''}`}
                      onClick={() => onToggleRow(row.id)}
                    >
                      <ChevronRightIcon />
                    </button>
                  </td>
                  <td>
                    <span className={styles.rfqPill}>{row.rfqNumber}</span>
                    {hasBookingCopy && (
                      <span className={styles.uploadedIndicator} title="Booking copy uploaded">
                        <CheckCircleIcon />
                      </span>
                    )}
                  </td>
                  <td><span className={styles.linerLink}>{row.shippingLiner}</span></td>
                  <td>{row.serviceProvider}</td>
                  <td>{row.from}</td>
                  <td>{row.to}</td>
                  <td>{row.containerType}</td>
                  <td><span className={styles.containerBadge}>{String(row.containers).padStart(2, '0')}</span></td>
                  <td>{row.deskType}</td>
                  <td>{row.incoTerm}</td>
                  <td><span className={styles.spotBadge}>{row.biddingEvent}</span></td>
                  <td>
                    <button className={styles.btnBookingCopy} onClick={() => onUploadBookingCopy(row, null)}>
                      <UploadIcon size={12} /> Upload Booking Copy
                    </button>
                  </td>
                </tr>
                {expandedRow === row.id && (
                  <tr>
                    <td colSpan={COLUMN_COUNT} style={{ padding: 0 }}>
                      <ExpandedDocSection
                        rfq={row}
                        onUploadBookingCopy={onUploadBookingCopy}
                        onUploadBL={onUploadBL}
                        onViewBL={onViewBL}
                        onSaveDoc={onSaveDoc}
                        onDeleteDoc={onDeleteDoc}
                        updateSubmitting={updateSubmitting}
                        deleteSubmitting={deleteSubmitting}
                        pendingDoc={pendingDocs && pendingDocs[row.id] ? pendingDocs[row.id].doc : null}
                        onSavePendingDoc={onSavePendingDoc}
                        onDiscardPendingDoc={onDiscardPendingDoc}
                        saveSubmitting={saveSubmitting}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
      <div className={styles.pagination}>
        <span>
          {total > 0
            ? `Showing ${from} to ${to} of ${total} entries`
            : 'No entries'}
        </span>
        <div className={styles.paginationBtns}>{renderPagination()}</div>
      </div>
    </div>
  );
};

export default BookingTable;
